import { Box, Text } from '@jfs/ui-new'
import { FC, ReactElement } from 'react'
import { ModBox } from '../../../comps/ModifiedBox/ModifiedBox'
import { TableComponent } from '../../../comps/TableComponent'
import { Address, AddressType } from '../../../data/models/Address'
import { LimitsV2 } from '../../../data/models/Limits'
import { MerchantType } from '../../../data/models/Merchant'
import { useWindowDimensions } from '../../../util/util'
import { styles } from './../styles'

interface FinanceProps {
  hide?: boolean
  data: MerchantType
  canViewTransactionLimit: boolean
  canViewSettlementBankDetails: boolean
}

const payAcceptanceData = (acceptedPayments: string[]) => {
  return [
    {
      id: '1',
      title: 'POS Credit Card/Debit Card',
      value: acceptedPayments?.includes('POS Credit Card/Debit Card') ? 'Yes' : 'No',
    },
    {
      id: '2',
      title: 'Online CC/DC/Net Banking',
      value: acceptedPayments?.includes('Online CC/DC/Net Banking') ? 'Yes' : 'No',
    },
    {
      id: '3',
      title: 'UPI',
      value: acceptedPayments?.includes('UPI') ? 'Yes' : 'No',
    },
    {
      id: '4',
      title: 'Wallets',
      value: acceptedPayments?.includes('Wallets') ? 'Yes' : 'No',
    },
    {
      id: '5',
      title: 'Sodexo',
      value: acceptedPayments?.includes('Sodexo') ? 'Yes' : 'No',
    },
    {
      id: '6',
      title: 'Supplier Payment Acceptance',
      value: acceptedPayments?.includes('Supplier Payment Acceptance') ? 'Yes' : 'No',
    },
    {
      id: '7',
      title: 'Hyperlocal Payment Acceptance',
      value: acceptedPayments?.includes('Hyperlocal Payment Acceptance') ? 'Yes' : 'No',
    },
  ]
}

const currTxnLimitData = (limits: LimitsV2[], dailyTransactionVolume: string): any[] => {
  return [
    { id: '1', title: 'JioMoney Wallet', value: limits[0]?.maxLimit ? `₹${limits[0]?.maxLimit}` : 'NA' },
    { id: '2', title: 'Debit/Credit Card (Rs.) :', value: limits[1]?.maxLimit ? `₹${limits[1]?.maxLimit}` : 'NA' },
    { id: '3', title: 'Txn Volume', value: dailyTransactionVolume ?? 'NA' },
  ]
}

const paySettleData = (merchant: MerchantType) => {
  const address = merchant?.contact?.addresses ? JSON.parse(JSON.stringify(merchant?.contact?.addresses)) : {}
  const { id, type, line1, line2, pincode, city, state, country } = address?.[0] ?? {}
  const add = Address.create({
    id: id ?? '0',
    type: type ?? AddressType.Business,
    line1: line1 ?? '',
    line2: line2 ?? '',
    pincode: pincode ?? '',
    city: city ?? '',
    state: state ?? '',
    country: country ?? '',
  })
  return [
    { id: '1', title: 'A/C Holder Name', value: merchant?.bankAccount?.bank?.name ?? 'NA' },
    { id: '2', title: 'Account Number', value: merchant?.bankAccount?.number ?? 'NA' },
    { id: '3', title: 'MID', value: merchant?.mid ?? 'NA' },
    { id: '4', title: 'Address', value: add?.printAddress?.() ?? 'NA' },
  ]
}

const Finance: FC<FinanceProps> = ({
  hide,
  data,
  canViewTransactionLimit,
  canViewSettlementBankDetails,
}: FinanceProps) => {
  const viewPortWidth = useWindowDimensions()

  const themedStyle = styles(viewPortWidth)

  if (hide) {
    return null
  }

  const getSeperator = (): ReactElement => {
    return <Box {...themedStyle.separatorStyle} />
  }

  const getHeader = (header: string): ReactElement => {
    return (
      <>
        <Box {...themedStyle.headerStyle} testID={header.replaceAll(' ', '')}>
          <Text {...themedStyle.headerTextStyle}>{header}</Text>
        </Box>
        <Box {...themedStyle.separatorStyle} />
      </>
    )
  }

  const renderListItem = ({ item }: any): ReactElement => {
    return (
      <Box {...themedStyle.listItemContainer}>
        <Text {...themedStyle.listItemKeyStyle} testID="key">
          {item?.title}
        </Text>
        <Text {...themedStyle.listItemValueStyle} testID={`financeTabView-${item?.title?.replaceAll(' ', '')}`}>
          {item?.value}
        </Text>
      </Box>
    )
  }

  return (
    <Box testID={'financeTabView'} {...themedStyle.dataParentContainer}>
      <Box {...themedStyle.dataParentColumnContainer}>
        <Box {...themedStyle.leftCardContainer}>
          <TableComponent
            value={''} //Routes value
            canFetchMore={false}
            ListContent={payAcceptanceData(data?.acceptedPayments)}
            hide={false}
            isCustom={true}
            RenderCustomElement={renderListItem}
            ItemSeparatorComponent={getSeperator}
            keyExtractor={(item) => item.id}
            ListHeaderComponent={getHeader('Payment Acceptance')}
          />
        </Box>
      </Box>
      <Box {...themedStyle.dataParentColumnContainer}>
        <ModBox show={canViewSettlementBankDetails} {...themedStyle.rightCardContainer}>
          <TableComponent
            value={''} //Routes value
            canFetchMore={false}
            ListContent={paySettleData(data)}
            hide={false}
            isCustom={true}
            RenderCustomElement={renderListItem}
            ItemSeparatorComponent={getSeperator}
            keyExtractor={(item) => item.id}
            ListHeaderComponent={getHeader('Payment and Settlements')}
          />
        </ModBox>
        <ModBox show={canViewTransactionLimit} {...themedStyle.rightCardContainer}>
          <TableComponent
            value={''} //Routes value
            canFetchMore={false}
            ListContent={currTxnLimitData(data?.limits, data?.dailyTransactionVolume)}
            hide={false}
            isCustom={true}
            RenderCustomElement={renderListItem}
            ItemSeparatorComponent={getSeperator}
            keyExtractor={(item) => item.id}
            ListHeaderComponent={getHeader('Current Transaction Limit')}
          />
        </ModBox>
      </Box>
    </Box>
  )
}
export default Finance
